// components/MemberPortal/ScheduleVerificationModal.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react';
import './MemberPortal.css';

/**
 * A dependency-free modal for selecting a verification call date/time & timezone.
 * Sends a local (naive) ISO without 'Z' to avoid unintended UTC shifts on the backend.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - onConfirm: ({ start_time, timezone, duration_minutes }) => Promise<void> | void
 * - defaultDuration: number (minutes, default 25)
 */
const ScheduleVerificationModal = ({
  isOpen,
  onClose,
  onConfirm,
  defaultDuration = 25,
}) => {
  const now = useMemo(() => new Date(), []);
  const [date, setDate] = useState(() => formatDateForInput(now)); // YYYY-MM-DD
  const [time, setTime] = useState(() => formatTimeForInput(addMinutes(now, 60))); // HH:MM
  const [duration, setDuration] = useState(defaultDuration);
  const [timezone, setTimezone] = useState(getBrowserTimeZone());
  const [submitting, setSubmitting] = useState(false);
  const overlayRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      // Reset on open
      const freshNow = new Date();
      setDate(formatDateForInput(freshNow));
      setTime(formatTimeForInput(addMinutes(freshNow, 60)));
      setDuration(defaultDuration);
      setTimezone(getBrowserTimeZone());
      // Focus trap-lite: focus first input
      setTimeout(() => {
        if (overlayRef.current) {
          const el = overlayRef.current.querySelector('input, select, button');
          if (el) el.focus();
        }
      }, 10);
      // Prevent body scroll
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = ''; };
    }
  }, [isOpen, defaultDuration]);

  const tzOptions = useMemo(() => {
    // Prefer the full IANA list if supported
    if (typeof Intl.supportedValuesOf === 'function') {
      try {
        const all = Intl.supportedValuesOf('timeZone') || [];
        // keep it manageable: prioritize US + common zones first
        const prioritized = [
          'America/New_York',
          'America/Chicago',
          'America/Denver',
          'America/Los_Angeles',
          'America/Phoenix',
          'America/Anchorage',
          'Pacific/Honolulu',
        ];
        const rest = all.filter((z) => !prioritized.includes(z));
        return [...prioritized, ...rest];
      } catch {
        /* fall through */
      }
    }
    // Fallback list
    return [
      'America/New_York',
      'America/Chicago',
      'America/Denver',
      'America/Los_Angeles',
      'America/Phoenix',
      'America/Anchorage',
      'Pacific/Honolulu',
      'UTC',
    ];
  }, []);

  const handleConfirm = async () => {
    if (!date || !time) return;
    // Build "local naive" ISO without trailing Z to avoid UTC conversion on backend parse
    // e.g., "2025-09-05T14:00:00"
    const start_time = `${date}T${time}:00`;
    try {
      setSubmitting(true);
      await onConfirm({
        start_time,
        timezone,
        duration_minutes: Number(duration) || defaultDuration,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="piq-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Schedule verification call"
      ref={overlayRef}
      onClick={(e) => {
        if (e.target === overlayRef.current) onClose();
      }}
    >
      <div className="piq-modal">
        <div className="piq-modal-header">
          <h3 className="piq-modal-title">Schedule Verification Call</h3>
          <button
            type="button"
            className="piq-icon-btn"
            aria-label="Close"
            onClick={onClose}
          >
            ×
          </button>
        </div>

        <div className="piq-modal-body">
          <div className="piq-field">
            <label className="piq-label">Date</label>
            <input
              className="piq-input"
              type="date"
              value={date}
              min={formatDateForInput(now)}
              onChange={(e) => setDate(e.target.value)}
            />
          </div>

          <div className="piq-field">
            <label className="piq-label">Time</label>
            <input
              className="piq-input"
              type="time"
              value={time}
              step="300"
              onChange={(e) => setTime(e.target.value)}
            />
          </div>

          <div className="piq-field">
            <label className="piq-label">Timezone</label>
            <select
              className="piq-input"
              value={timezone}
              onChange={(e) => setTimezone(e.target.value)}
            >
              {tzOptions.map((z) => (
                <option key={z} value={z}>{z}</option>
              ))}
            </select>
          </div>

          <div className="piq-field">
            <label className="piq-label">Duration (minutes)</label>
            <input
              className="piq-input"
              type="number"
              min={5}
              max={90}
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </div>
        </div>

        <div className="piq-modal-footer">
          <button
            type="button"
            className="piq-btn-secondary"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="piq-btn-primary"
            onClick={handleConfirm}
            disabled={submitting || !date || !time}
          >
            {submitting ? 'Scheduling…' : 'Schedule'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleVerificationModal;

/* ---------- helpers ---------- */

function getBrowserTimeZone() {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/New_York';
  } catch {
    return 'America/New_York';
  }
}

function pad2(n) {
  return String(n).padStart(2, '0');
}

function formatDateForInput(d) {
  const y = d.getFullYear();
  const m = pad2(d.getMonth() + 1);
  const day = pad2(d.getDate());
  return `${y}-${m}-${day}`;
}

function formatTimeForInput(d) {
  const h = pad2(d.getHours());
  const min = pad2(d.getMinutes());
  return `${h}:${min}`;
}

function addMinutes(d, minutes) {
  const copy = new Date(d.getTime());
  copy.setMinutes(copy.getMinutes() + minutes);
  return copy;
}
